import helpers from '../helpers.js';

//@TODO refactor the BelongsToManyCache into a seperate module with better documentation

/**
 * @ngdoc service
 * @name BaseClass.BCCacheable
 * @description
 * Adds caching logic to models. The cache itself is kept as static property on the model constructor
 */
export default angular.module('BaseClass.cacheable',[])
    .factory('BCCacheable', function($injector) {
    /*
     * Mixin class that adds Caching functionality to models
     */
        function Cache() {
        /**
         * @ngdoc object
         * @name BaseClass.BCCacheable#cached
         * @propertyOf BaseClass.BCCacheable
         * @static
         * @description
         * Object containing all the cached models, keyed by the model's primary key.
         *
         * Additional functions are sneakily hidden in this object.
         *
         * <pre>
         * Model.cached.first();
         * Model.cached.clear();
         * Model.cached.cache(instance);
         * Model.cached.where(query = {key: value});
         * Model.cached.remove(query);
         * Model.cached.isCached(query);
         * </pre>
         */
            var cached = {};

            /**
         * @ngdoc object
         * @name BaseClass.BCCacheable#cachedQueries
         * @propertyOf BaseClass.BCCacheable
         * @static
         * @description
         * Array containing search queries that have run for a model
         *
         * Additional functions are sneakily hidden in this object.
         *
         * <pre>
         * Model.cachedQueries.cache(query);
         * Model.cachedQueries.isCached(query);
         * </pre>
         */
            var cachedQueries = [];


            /**
         * An object for caching many-to-many relationships.
         *
         * Frontend can't easily query pivot tables, so we recreate them
         * using caching techniques. The objects are cached on their
         * respective Constructors and this cache will hold a reference
         * to those cached instances, along with optional pivot
         * information.
         *
         * @constructor
         * @param {Function} ParentCache The constructor function this cache is attached to
         */
            function BelongsToManyCache(ParentCache) {
                Object.defineProperty(this, 'ParentCache', {
                    value: ParentCache,
                    enumerable: false
                });
            }


            /**
         * Cache a child relation
         *
         * @param {String|Number} primaryKey The parents primary key
         * @param {String} modelName Name of the child's class
         * @param {Object} model The child
         * @param {Object} pivot Optional, pivot information associated with the unique link between parent/child
         */
            helpers.privateVariable(BelongsToManyCache.prototype, 'cache', function(primaryKey, modelName, model, pivot){
                if(!this[primaryKey][modelName])
                    this[primaryKey][modelName] = [];
                pivot = pivot || model.pivot;
                var pk = model.constructor.primaryKey;
                var cached = _.find(this[primaryKey][modelName], { model: model[pk] });
                if(cached)
                    cached.pivot = pivot;
                else
                    this[primaryKey][modelName].push({ model: model[pk], pivot: pivot });
            });

            /**
         * Restore models from the cache, with their respective pivot information
         *
         * @param {String|Number} primaryKey The parents primary key
         * @param {String} modelName Name of the relationship
         * @returns {Object[]} Array of restored models
         */
            helpers.privateVariable(BelongsToManyCache.prototype, 'restore', function(primaryKey, modelName) {
                if(!this[primaryKey][modelName]) return [];
                return _.map(this[primaryKey][modelName], function(cacheObject) {
                    var Model = $injector.get(modelName);
                    /**
                     * **WARNING**
                     *
                     * We must make a new Model, as not to disturb other copies that are in memory.
                     *
                     * Lets take the hypothetical situation where A belongs to both B and C. If we
                     * want to display both of them, with their correct pivot information, we need
                     * *two* copies in memory. So we simply clone the cached model and set the
                     * correct pivot info
                     */
                    var model = Model.new(Model.cached[cacheObject.model]);
                    model.pivot = cacheObject.pivot;
                    return model;
                });
            });

            /**
         * Remove a child from cache
         *
         * @param {String|Number} primaryKey The parents primary key
         * @param {String} modelName Name of the child's class
         * @param {Object} model The child
         */
            helpers.privateVariable(BelongsToManyCache.prototype, 'remove', function(primaryKey, modelName, model){
                if(!this[primaryKey][modelName]) return;
                var pk = model.constructor.primaryKey;
                _.remove(this[primaryKey][modelName], {model: model[pk]});
            });

            /**
         * Check if a child is cached
         *
         * @param {String|Number} primaryKey The parents primary key
         * @param {String} modelName Name of the child's class
         * @param {Object} model The child
         */
            helpers.privateVariable(BelongsToManyCache.prototype, 'isCached', function(primaryKey, modelName, model){
                if(!this[primaryKey][modelName]) return false;
                var pk = model.constructor.primaryKey;
                var cached = _.find(this[primaryKey][modelName], { model: model[pk] });
                return !_.isUndefined(cached);
            });

            /**
         * Clear the belongsToManyCache
         *
         * @param {String|Number} [primaryKey] Optional primary key
         * @param {String} [modelName] Optional name of child's class
         */
            helpers.privateVariable(BelongsToManyCache.prototype, 'clear', function(primaryKey, relationship){
                if(!relationship && !primaryKey) {
                    _.forEach(_.keys(this), pk => {
                        _.unset(this, pk);
                    });
                    return;
                }
                _.unset(this, Array.prototype.slice.call(arguments,0));
            });

            /**
         * Reverse lookup, find parents for child
         *
         * @param {String} modelName Name of the child's class
         * @param {Object} model The child
         * @returns {String[]} Array of parent_ids
         */
            helpers.privateVariable(BelongsToManyCache.prototype, 'findParents', function(modelName, model){
                const cache = this;
                var childId = model[model.constructor.primaryKey];
                var parentIds = _.keys(this);
                var matches = _.filter(parentIds, parentId => {
                    return _.find(cache[parentId][modelName], {model: childId});
                });
                return _.map(matches, guid => {
                    return cache.ParentCache.cached[guid];
                });
            });

            /**
         * Cache a query
         *
         * @param {any} query
         */
            helpers.privateVariable(cachedQueries, 'cache',function(query) {
                cachedQueries.push(query);
                cachedQueries = _.uniq(cachedQueries, function(a) {
                    return JSON.stringify(a);
                });
            });

            /**
         * Check if query is cached, implying that the corresponding models are also cached
         *
         * @param {any} query
         * @return {boolean}
         */
            helpers.privateVariable(cachedQueries,'isCached', function(query) {
                return _.findIndex(cachedQueries, function(q) {
                    return JSON.stringify(q) === JSON.stringify(query);
                }) > -1;
            });

            /**
         * Clear the query cache
         */
            helpers.privateVariable(cachedQueries,'clear', function() {
                Array.prototype.splice.call(cachedQueries, 0, cachedQueries.length);
            });

            /**
         * Remove item from cache
         *
         * @param {any} primaryKey
         */
            helpers.privateVariable(cached, 'remove', function(primaryKey) {
                delete cached[primaryKey];
            });

            /**
         * Add item to cache
         *
         * @param {Object} instance An object with a primaryKey
         */
            helpers.privateVariable(cached, 'cache', function(instance) {
                var primaryKey = this.primaryKey;
                if (instance && instance[primaryKey] !== undefined)
                    cached[instance[primaryKey]] = instance;
            });

            /**
         * Clear cache
         */
            helpers.privateVariable(cached, 'clear', function() {
                Object.keys(cached).forEach(function(key) {
                    delete cached[key];
                });
            });

            /**
         * Check if cache is empty
         *
         * @return {boolean}
         */
            helpers.privateVariable(cached, 'isEmpty', function() {
                return !!(!Object.keys(cached).length);
            });

            /**
         * Return the first item from cache
         *
         * @return {Object}
         */
            helpers.privateVariable(cached, 'first', function() {
                if(!cached.isEmpty())
                    return cached[_.first(Object.keys(cached))];
                return undefined;
            });

            /**
         * Return the amount of items in cache
         *
         * @return {Object}
         */
            helpers.privateVariable(cached, 'size', function() {
                return _.size(this);
            });

            /**
         * Select objects from cache based on criteria
         *
         * @param {Object|Function} terms The criteria to match against, runs them using Lodash#where
         * @return {Array} Objects matching the criteria
         */
            helpers.privateVariable(cached, 'where', function(terms) {
                if(typeof this.whereCustom === 'function')
                    return this.whereCustom(terms);
                return _.filter(cached, terms);
            });

            this.cached = cached;
            this.cache = this.cached.cache;
            this.cachedQueries = cachedQueries;
            this.belongsToManyCache = new BelongsToManyCache(this);
            this.filterCache = {};

            /**
         * @ngdoc function
         * @name BaseClass.BCCacheable#createBelongsToManyCache
         * @methodOf BaseClass.BCCacheable
         * @description
         * Create a cache for an instance, this cache is automagically created for models
         *
         * @param {Object} instance Model instance
         * @returns {Object}
         * A subset of the belongsToManyCache containing this instances relationships plus some functionality
         *
         * <pre>
         * var cache = instance.belongsToMany;
         * angular.isArray(cache['Relationship']) == true;
         * cache['Relationship'][0] // { model: 'guid', pivot: {...} };
         *
         * cache.cache('Relationship', foreignModel, pivot = {});
         * cache.restore('Relationship'); // Array
         * cache.remove('Relationship', foreignModel);
         * cache.isCached('Relationship', foreignModel); // Boolean
         * cache.clear(['Relationship']) // Clear one or more child caches
         * Model.findParents('Relationship', child); // Array<Model>
         * </pre>
         */
            this.createBelongsToManyCache = instance => {
                var Class = instance.constructor;
                var instanceCache = Class.belongsToManyCache[instance[Class.primaryKey]] || {};
                var pkGetter = () => {
                    let pk = instance[Class.primaryKey];
                    if(!pk) return null;
                    if(!Class.belongsToManyCache[pk])
                        Class.belongsToManyCache[pk] = instanceCache;
                    return pk;
                };
                ['cache','restore','remove','clear','isCached'].forEach(fn => {
                    if(instanceCache[fn]) return;
                    helpers.privateVariable(instanceCache, fn, function(...args) {
                        let pk = pkGetter();
                        if(!pk) return [];
                        return Class.belongsToManyCache[fn](pk, ...args);
                    });
                });
                return instanceCache;
            };
        }

        return Cache;
    }).name;
